import { ClientOnly, createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { Layout } from "@/components/Layout";
import { GlobalHero } from "@/components/GlobalHero";
import { AdSenseShield } from "@/components/AdSenseShield";
import { SiloedZonesGrid } from "@/components/SiloedZonesGrid";
import { DisplayAd } from "@/components/DisplayAd";
import { computeCityPrices, formatMoney } from "@/lib/gold-data";
import { useGoldRates, goldRatesQueryOptions } from "@/lib/nonblocking-rates";
import { CrawlerLinksStrip, crawlerLinksQueryOptions } from "@/components/CrawlerLinksStrip";

const HomeCalculatorTabs = lazy(() =>
  import("@/components/HomeCalculatorTabs").then((m) => ({ default: m.HomeCalculatorTabs })),
);
const Watchlist = lazy(() =>
  import("@/components/Watchlist").then((m) => ({ default: m.Watchlist })),
);

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "DrHint — Real-Time Global Utilities & Financial Toolbox" },
      {
        name: "description",
        content:
          "Geo-neutral smart dashboard for live precious-metal markets, loan calculators and honest online-work guides.",
      },
      { property: "og:title", content: "DrHint — Real-Time Global Utilities & Financial Toolbox" },
      { property: "og:description", content: "Live metals, loan EMI and online-work guides — one geo-neutral toolbox." },
      { property: "og:url", content: "https://drhint.com/" },
    ],
    links: [{ rel: "canonical", href: "https://drhint.com/" }],
    scripts: buildHomeJsonLd(),
  }),
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    void context.queryClient.prefetchQuery(crawlerLinksQueryOptions("home"));
  },
  component: HomePage,
});

function buildHomeJsonLd() {
  const SITE = "https://drhint.com";
  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How often is the gold rate on DrHint updated?",
        acceptedAnswer: { "@type": "Answer", text: "Every 10 minutes. The 24K, 22K and 21K rates are recalculated from the international XAU/USD spot price multiplied by the live USD/PKR, USD/INR or USD/AED exchange rate, then split per gram, per tola and per 10 gram." },
      },
      {
        "@type": "Question",
        name: "Which countries does DrHint cover for live precious-metal rates?",
        acceptedAnswer: { "@type": "Answer", text: "Pakistan, India, UAE and Saudi Arabia have dedicated live gold-rate hubs. Silver / chandi rates are published for 23 Pakistani and Indian cities. The free Gold Rate API serves all of them as a JSON feed." },
      },
      {
        "@type": "Question",
        name: "Is DrHint free and does it require signup?",
        acceptedAnswer: { "@type": "Answer", text: "Every calculator and rate hub on DrHint is free and works without an account. No reference number, OTP or personal data is stored on our servers." },
      },
      {
        "@type": "Question",
        name: "Where does the live spot price come from?",
        acceptedAnswer: { "@type": "Answer", text: "International XAU/USD and XAG/USD spot prices are sourced from public bullion APIs; FX rates are sourced from open-banking aggregators. Both feeds are polled every 10 minutes and surfaced through the Free Gold Rate API for transparency." },
      },
    ],
  };
  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "DrHint main hubs",
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: 7,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Live Gold Rate Pakistan", url: `${SITE}/pakistan` },
      { "@type": "ListItem", position: 2, name: "Live Gold Rate India", url: `${SITE}/india` },
      { "@type": "ListItem", position: 3, name: "Live Gold Rate UAE", url: `${SITE}/uae` },
      { "@type": "ListItem", position: 4, name: "Non-Phone Remote Jobs", url: `${SITE}/remote-jobs` },
      { "@type": "ListItem", position: 5, name: "Gold Tools Suite", url: `${SITE}/tools` },
      { "@type": "ListItem", position: 6, name: "All Calculators", url: `${SITE}/calculators` },
      { "@type": "ListItem", position: 7, name: "Free Gold Rate API", url: `${SITE}/developer/gold-rate-api` },
    ],
  };
  const dataset = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: "DrHint Live Precious-Metal Rates",
    description: "Real-time 24K, 22K and 21K gold rates plus XAG silver rates for Pakistan, India, UAE and Saudi Arabia. Recomputed every 10 minutes from international spot price and live FX.",
    keywords: ["gold rate", "silver rate", "XAU USD", "XAG USD", "PKR", "INR", "AED", "tola", "10 gram"],
    license: "https://creativecommons.org/licenses/by/4.0/",
    isAccessibleForFree: true,
    creator: { "@type": "Organization", name: "DrHint", url: `${SITE}/` },
    distribution: [
      { "@type": "DataDownload", encodingFormat: "application/json", contentUrl: `${SITE}/developer/gold-rate-api` },
    ],
    temporalCoverage: "2020-01-01/..",
    spatialCoverage: [
      { "@type": "Place", name: "Pakistan", geo: { "@type": "GeoShape", box: "23.6345 60.8729 37.0841 77.8375" } },
      { "@type": "Place", name: "India", geo: { "@type": "GeoShape", box: "6.7548 68.1622 35.6745 97.3956" } },
      { "@type": "Place", name: "United Arab Emirates", geo: { "@type": "GeoShape", box: "22.6333 51.5833 26.0833 56.3833" } },
      { "@type": "Place", name: "Saudi Arabia", geo: { "@type": "GeoShape", box: "16.0000 34.5000 32.1500 55.6667" } },
    ],
  };
  // Site identity — enables sitelinks search box eligibility and gives the
  // `${SITE}/#website` / `#organization` @id references used site-wide a target.
  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE}/#website`,
    url: `${SITE}/`,
    name: "DrHint",
    publisher: { "@id": `${SITE}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: { "@type": "EntryPoint", urlTemplate: `${SITE}/search?q={search_term_string}` },
      "query-input": "required name=search_term_string",
    },
  };
  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE}/#organization`,
    name: "DrHint",
    url: `${SITE}/`,
    logo: `${SITE}/icon-512.png`,
  };
  return [
    { type: "application/ld+json" as const, children: JSON.stringify(website) },
    { type: "application/ld+json" as const, children: JSON.stringify(organization) },
    { type: "application/ld+json" as const, children: JSON.stringify(faq) },
    { type: "application/ld+json" as const, children: JSON.stringify(itemList) },
    { type: "application/ld+json" as const, children: JSON.stringify(dataset) },
  ];
}

function HomePage() {
  return (
    <Layout>
      <div className="w-full max-w-full overflow-x-hidden">
        <GlobalHero />

        <SiloedZonesGrid />

      <div className="mx-auto max-w-6xl px-4">
        <DisplayAd placement="mid-billboard" className="my-8" />
      </div>

      <div className="cv-auto">
      <ClientOnly fallback={<div style={{ minHeight: 620 }} aria-hidden="true" />}>
        <Suspense fallback={<div style={{ minHeight: 620 }} aria-hidden="true" />}>
          <HomeCalculatorTabs />
        </Suspense>
      </ClientOnly>

      <ClientOnly fallback={<div style={{ minHeight: 360 }} aria-hidden="true" />}>
        <Suspense fallback={<div style={{ minHeight: 360 }} aria-hidden="true" />}>
          <GeoSnapshot />
        </Suspense>
      </ClientOnly>

      <div className="mx-auto max-w-6xl px-4">
        <DisplayAd placement="inline-2" className="my-8" />
      </div>

      <ClientOnly fallback={<div style={{ minHeight: 420 }} aria-hidden="true" />}>
        <Suspense fallback={<div style={{ minHeight: 420 }} aria-hidden="true" />}>
          <Watchlist />
        </Suspense>
      </ClientOnly>

      <div className="mx-auto max-w-6xl px-4">
        <DisplayAd placement="related-multiplex" className="my-8" />
      </div>

      <CrawlerLinksStrip page="home" />

      <AdSenseShield />
      </div>
      </div>
    </Layout>
  );
}

/** Above-the-fold semantic HTML table for GEO/AI crawlers — first 100 words. */
function GeoSnapshot() {
  const { data } = useGoldRates();
  const spot = Number.isFinite(data?.spotUsdPerOz) ? data.spotUsdPerOz : 0;
  const pkFx = Number.isFinite(data?.pkrPerUsd) ? data.pkrPerUsd : 0;
  const inFx = Number.isFinite(data?.inrPerUsd) ? data.inrPerUsd : 0;
  const pk = computeCityPrices({ spotUsdPerOz: spot, fxRate: pkFx, country: "pakistan", cityPremium: 1 });
  const ind = computeCityPrices({ spotUsdPerOz: spot, fxRate: inFx, country: "india", cityPremium: 1 });
  const fetchedAt = data?.fetchedAt ? new Date(data.fetchedAt) : new Date();
  const date = Number.isFinite(fetchedAt.getTime())
    ? fetchedAt.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })
    : "today";
  const spotText = spot > 0 ? `$${spot.toFixed(2)}/oz` : "the live international spot feed";

  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <div className="glass rounded-3xl p-6 md:p-8">
        <h2 className="text-2xl md:text-3xl font-bold mb-2">
          Today's Gold Rate Snapshot — <span className="text-gold" data-nosnippet>{date}</span>
        </h2>
        <p className="text-sm text-muted-foreground mb-5 max-w-3xl">
          Live 24K, 22K and 21K gold prices in Pakistan (PKR per tola) and India (INR per 10g),
          recalculated every 10 minutes from <b>{spotText}</b>.
        </p>
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-card/60 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Country</th>
                <th className="px-4 py-3 font-semibold">Unit</th>
                <th className="px-4 py-3 font-semibold text-right">24K</th>
                <th className="px-4 py-3 font-semibold text-right">22K</th>
                <th className="px-4 py-3 font-semibold text-right">21K</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border">
                <td className="px-4 py-3 font-medium">🇵🇰 Pakistan</td>
                <td className="px-4 py-3 text-muted-foreground">per Tola</td>
                <td className="px-4 py-3 text-right font-bold text-gold">{formatMoney(pk.perTola24, "PKR")}</td>
                <td className="px-4 py-3 text-right">{formatMoney(pk.perTola22, "PKR")}</td>
                <td className="px-4 py-3 text-right">{formatMoney(pk.perTola21, "PKR")}</td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-3 font-medium">🇮🇳 India</td>
                <td className="px-4 py-3 text-muted-foreground">per 10g</td>
                <td className="px-4 py-3 text-right font-bold text-gold">{formatMoney(ind.per10g24, "INR")}</td>
                <td className="px-4 py-3 text-right">{formatMoney(ind.per10g22, "INR")}</td>
                <td className="px-4 py-3 text-right">{formatMoney(ind.per10g21, "INR")}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <Link to="/pakistan" className="inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/30 text-primary px-3 py-1.5 font-medium hover:bg-primary/20">See current gold rates in Pakistan</Link>
          <Link to="/india" className="inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/30 text-primary px-3 py-1.5 font-medium hover:bg-primary/20">Full India breakdown →</Link>
        </div>
      </div>
    </section>
  );
}
